import React, { useEffect, useRef, useState } from 'react';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import styles from './RecoverPasswordToken.module.css';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@hooks/useNotifications';
import { useValidateForgotPasswordToken } from '@services/useValidateForgotPasswordToken';

export interface RecoverPasswordTokenProps {
    email: string;
    onNext: (token: string) => void;
}

const TOKEN_LENGTH = 6;

export const RecoverPasswordToken: React.FC<RecoverPasswordTokenProps> = ({ email, onNext }) => {
    const { t } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [otp, setOtp] = useState<string[]>(Array(TOKEN_LENGTH).fill(''));

    const { showDefaultNotification } = useNotifications();
    const { request, error, success, lastResponseTime, loading } = useValidateForgotPasswordToken();

    const translations = {
        title: t('recover.token.title'),
        description: t('recover.token.description'),
        submit: t('recover.token.submit'),
    };

    useEffect(() => {
        if (otp.every((d) => d !== '')) {
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp]);

    useEffect(() => {
        if (!lastResponseTime) return;

        if (error) {
            showDefaultNotification({ type: 'error', message: error });
            return;
        }

        if (success) {
            onNext(otp.join(''));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const focusInput = (index: number) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (inputs && inputs[index]) {
            (inputs[index] as HTMLInputElement).focus();
        }
    };

    const handleChange = (value: string, index: number) => {
        const chars = value.replace(/[^a-zA-Z0-9]/g, '');
        if (!chars) return;

        const updated = [...otp];
        updated[index] = chars[0];

        for (let i = 1; i < chars.length && index + i < TOKEN_LENGTH; i++) {
            updated[index + i] = chars[i];
        }

        setOtp(updated);

        const nextIndex = index + chars.length;
        if (nextIndex < TOKEN_LENGTH) {
            focusInput(nextIndex);
        }
    };

    const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (!inputs) return;

        const active = document.activeElement;
        const index = Array.from(inputs).findIndex((i) => i === active);

        if (index === -1) return;

        if (e.key === 'Backspace') {
            e.preventDefault();
            const updated = [...otp];

            if (otp[index]) {
                updated[index] = '';
            } else if (index > 0) {
                updated[index - 1] = '';
                focusInput(index - 1);
            }

            setOtp(updated);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '');

        if (!pasted) return;

        const digits = pasted.slice(0, TOKEN_LENGTH).split('');
        const updated = [...otp];
        digits.forEach((d, i) => (updated[i] = d));
        setOtp(updated);
    };

    const handleSubmit = () => {
        if (loading) return;

        const joined = otp.join('');
        if (joined.length !== TOKEN_LENGTH || otp.includes('')) return;

        request(joined);
    };

    return (
        <Form formRef={formRef} classes="d-flex flex-column gap-2" onEnterPress={handleSubmit} onSubmit={handleSubmit}>
            <Typography align="center" fontSize="24" fontWeight="800">
                {translations.title}
            </Typography>

            <Typography align="center" fontSize="16" color="muted">
                {translations.description}
            </Typography>

            <Typography align="center" fontSize="16" fontWeight="800" color="muted">
                {email}
            </Typography>

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <div className={styles.otpOuterWrapper}>
                <div ref={containerRef} onPaste={handlePaste} onKeyDown={handleContainerKeyDown} className={styles.otpRow}>
                    {otp.map((digit, index) => (
                        <Input
                            key={index}
                            type={InputTypes.text}
                            variant={InputVariants.regular}
                            value={digit}
                            onChange={(value) => handleChange(value, index)}
                            maxLength={1}
                            classes="text-center"
                        />
                    ))}
                </div>
            </div>

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            <Button label={translations.submit} mode={ButtonMode.primary} onClick={handleSubmit} disabled={loading} />
        </Form>
    );
};
